import useFullSizeModalRouteUpdater from 'hooks/useFullSizeModalRouteUpdater'
import useModal from 'hooks/useModal'
import { RowMiddle } from 'layout/Components/Row'
import { useMemo } from 'react'
import { Trans } from 'react-i18next'
import { Icons } from 'svgs'
import theme from 'theme'
import { ModalBody, ModalHeader, ModalProps } from 'UIKit/AlchemyModal'
import { ModalSizeModeEnum } from 'UIKit/AlchemyModal/Modal'
import IconButton from 'UIKit/Button/IconButton'
import Text from 'UIKit/Text'
import { PaymentPageEnums } from 'views/Payment/Menu'
import DepositTransactionList from 'views/Profile/Transactions/components/Deposit/DepositTransactionList'
import WithdrawTransactionList from 'views/Profile/Transactions/components/Withdraw/WithdrawTransactionList'
import { PAYMENT_MODAL_THEME, PaymentChevronRightIcon, PaymentInputContainer, StylePaymentModal } from '../styles'
import FindDepositModal from './FindDepositModal'
import { initialDataPaymentTransaction, IPaymentTransactionFilter, useTransactionFilter } from './hooks'
import PaymentHistoryFilterModal from './PaymentHistoryFilterModal'

export const PaymentHistoryButton: React.FC<{
  from: PaymentPageEnums
  hideFindHash?: boolean
}> = ({ from, hideFindHash }) => {
  const [onPresent] = useModal(PaymentHistoryModal)

  return (
    <RowMiddle
      height="24px"
      onClick={() => {
        onPresent({
          from,
          hideFindHash,
        })
      }}
    >
      <Icons.HistoryIcon width="24px" height="24px" color={theme.colors.text_primary} />
    </RowMiddle>
  )
}

const PaymentHistoryModal: React.FC<
  ModalProps<{
    from: PaymentPageEnums
    hideFindHash?: boolean
  }>
> = ({ data: { from, hideFindHash }, onDismiss }) => {
  const onBack = useFullSizeModalRouteUpdater(`PH-${from}`, onDismiss)
  const [onPresent] = useModal(PaymentHistoryFilterModal)
  const [onPresentFindDeposit] = useModal(FindDepositModal)
  const control = useTransactionFilter<IPaymentTransactionFilter>({
    initialData: initialDataPaymentTransaction,
  })

  const filters = useMemo(() => {
    return {
      currency: control.filters.currency,
      dateRange: [new Date(control.filters.fromDate), new Date(control.filters.toDate)],
      status: control.filters.status,
    }
  }, [control.filters])

  return (
    <StylePaymentModal sizeMode={ModalSizeModeEnum.Full} colorTheme={PAYMENT_MODAL_THEME} onDismiss={onBack}>
      <ModalHeader
        colorTheme={PAYMENT_MODAL_THEME}
        onBack={onBack}
        hideClose
        title={
          from === PaymentPageEnums.Deposit
            ? 'Deposit History'
            : from === PaymentPageEnums.Withdraw
            ? 'Withdraw History'
            : 'Unknown'
        }
        titleAlign="center"
        useBackIcon
        suffixNode={
          <>
            <IconButton
              noIconStyle
              onClick={() => {
                onPresent({
                  control,
                })
              }}
              variant="text"
            >
              <Icons.OutlineFilterIcon color={control.isFiltering ? theme.colors.primary : theme.colors.text_primary} />
            </IconButton>
          </>
        }
      />
      <ModalBody overflowY="scroll" gap="20px">
        {from === PaymentPageEnums.Deposit ? (
          <>
            {!hideFindHash && (
              <PaymentInputContainer
                onClick={onPresentFindDeposit}
                minHeight="var(--pm-trigger-height)"
                p="12px"
                color="var(--pm-label-color)"
                justifyContent="space-between"
              >
                <RowMiddle gap="8px">
                  <Icons.PaymentDepositIcon color="currentColor" />
                  <Text small color="currentColor">
                    <Trans>Can't find your recent deposit?</Trans>
                  </Text>
                </RowMiddle>
                <PaymentChevronRightIcon color="currentColor" />
              </PaymentInputContainer>
            )}

            <DepositTransactionList filters={filters} />
          </>
        ) : (
          <WithdrawTransactionList filters={filters} />
        )}
      </ModalBody>
    </StylePaymentModal>
  )
}

export default PaymentHistoryModal
